import { SET_PRIMARY_COLOR, SET_SECONDARY_COLOR, SET_FONT, SET_LOGO, RESET_THEME, SET_INITIAL_THEME, SET_LANGUAGE, SET_VENDOR_NAME, SET_TEXT_SECONDARY_COLOR, SET_PRODUCTS_ORDER, SET_ORDER_BUTTON_LABEL, SET_ORDER_BUTTON_ACTION, SET_UNIT_DISPLAY, SET_CORNER_RADIUS, SET_VISUALIZER_FILTERS, SET_PRODUCT_CARD_FIELDS , SET_ANALYTICS_ENABLED } from "./settingsActionTypes";
import * as _ from "lodash"
import { defaultFonts, defaultLogo, defaultPrimaryColor, defaultSecondaryColor, defaultTextSecondaryColor } from "../../../theme";

export interface CardItemConfig {
    key: string;
    style?: string;
    overlay?: string;
    concatWithPrevious?: boolean;
    separator?: string;
    badgeBgColor?: string;
    badgeTextColor?: string;
}

interface Theme {
    primaryColor: string
    secondaryColor: string
    textSecondaryColor: string
    font: string
    logo: string,
    language: string,
    vendorName: string
    orderButtonLabel: string
    orderButtonAction: string
    /**
     * False only when the vendor has contractually opted out of measurement. Absent or
     * true means measurement is on.
     *
     * Note the default is the OPPOSITE of the visualizer's: there, an unknown tenant is
     * treated as opted out so nothing leaks while the config is in flight. Here the only
     * consequence is which menus render, so an unknown vendor shows everything rather
     * than blanking the navigation on every page load.
     */
    analyticsEnabled: boolean
    unitDisplay: string
    cornerRadius: string
    visualizerFilters: string[]
    productCardFields: CardItemConfig[]
}

export interface GeneralState {
    initialTheme?: Theme
    theme?: Theme
    productsOrder: string
}

const initialState: GeneralState = {
    theme: {
        primaryColor: defaultPrimaryColor,
        secondaryColor: defaultSecondaryColor,
        textSecondaryColor: defaultTextSecondaryColor,
        font: defaultFonts,
        logo: defaultLogo,
        language: "en",
        vendorName: "ArDealy",
        orderButtonLabel: "Go to product",
        orderButtonAction: "link",
        analyticsEnabled: true,
        unitDisplay: "cm",
        cornerRadius: "8",
        visualizerFilters: ["Color", "Size", "Collection"],
        productCardFields: [
            { key: "name" },
            { key: "brand" },
            { key: "color" },
            { key: "size" }
        ]
    },
    productsOrder: "DATE+1"
};

const settingsTempReducer = (state = initialState, action): GeneralState => {
    switch (action.type) {
        case SET_INITIAL_THEME:
            return {
                ...state,
                initialTheme: { ...state.theme }
            }
        case SET_TEXT_SECONDARY_COLOR:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    textSecondaryColor: action.payload
                }
            }
        case SET_PRIMARY_COLOR:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    primaryColor: action.payload
                }
            }
        case SET_SECONDARY_COLOR:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    secondaryColor: action.payload
                }
            }
        case SET_FONT:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    font: action.payload
                }
            }
        case SET_LOGO:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    logo: action.payload
                }
            }
        case SET_LANGUAGE:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    language: action.payload
                }
            }
        case SET_VENDOR_NAME:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    vendorName: action.payload
                }
            }
        case RESET_THEME:
            return {
                ...state,
                theme: { ...state.initialTheme }
            }
        case SET_PRODUCTS_ORDER:
            return {
                ...state,
                productsOrder: action.payload
            }
        case SET_ORDER_BUTTON_LABEL:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    orderButtonLabel: action.payload
                }
            }
        case SET_ORDER_BUTTON_ACTION:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    orderButtonAction: action.payload
                }
            }
        case SET_ANALYTICS_ENABLED:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    analyticsEnabled: action.payload
                }
            }
        case SET_UNIT_DISPLAY:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    unitDisplay: action.payload
                }
            }
        case SET_CORNER_RADIUS:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    cornerRadius: action.payload
                }
            }
        case SET_VISUALIZER_FILTERS:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    visualizerFilters: action.payload
                }
            }
        case SET_PRODUCT_CARD_FIELDS:
            return {
                ...state,
                theme: {
                    ...state.theme,
                    productCardFields: action.payload
                }
            }
        default:
            return state
    }
};

export default settingsTempReducer;